import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useIonToast } from '@ionic/react'
import { SendIntent } from 'send-intent'

import { obtainTikTokVideoMetadata } from '@/services/tikTok/index'
import { Routes } from '@/router/routes'
import { isNative } from '@/utils/utils'

import { useUserInfo } from '../useUserInfo'
import { isAudio, isValidData, normalizedIntentMessage } from './helper'

import { UgcComposerWebMedia } from '@/plugins/ugc-composer'

export const useShareIntentReceiver = () => {
  const { data: authUser } = useUserInfo()

  const { t } = useTranslation('common')
  const history = useHistory()
  const [presentToast, dismiss] = useIonToast()

  const checkIntentMessage = useCallback(async () => {
    if (!authUser) return

    try {
      const intentMessage = await SendIntent.checkSendIntentReceived()

      const intentMessageNormalized = normalizedIntentMessage(intentMessage)

      if (!intentMessageNormalized.url) return

      if (!isValidData(intentMessageNormalized)) {
        await presentToast({
          position: 'middle',
          header: t('tikTokSharing.sharingNonTitTikMessage'),
          duration: 3000,
        })
        return
      }

      if (intentMessageNormalized.url) {
        await presentToast({
          position: 'middle',
          header: t('tikTokSharing.sharingInProgress'),
        })
      }

      const { videoNoWaterMarkUrl = '', caption = '' } =
        await obtainTikTokVideoMetadata(intentMessageNormalized.url)

      await dismiss()

      if (!videoNoWaterMarkUrl) {
        await presentToast({
          position: 'middle',
          header: t('tikTokSharing.sharingExceptionMessage'),
          duration: 3000,
        })
        return
      }

      if (isAudio(videoNoWaterMarkUrl)) {
        await presentToast({
          position: 'middle',
          header: t('tikTokSharing.sharingNonVideoMessage'),
          duration: 3000,
        })
        return
      }

      const media: UgcComposerWebMedia = {
        url: videoNoWaterMarkUrl,
        ratio: 9 / 16,
        mimeType: 'video/mp4',
        mediaType: 'video',
        caption: caption,
        isExternalMedia: true,
      }

      history.push({
        pathname: Routes.POST,
        state: {
          media,
        },
      })
    } catch (error) {
      console.error(error)
    }
  }, [authUser, dismiss, history, presentToast, t])

  useEffect(() => {
    if (!isNative()) return
    checkIntentMessage()
    window.addEventListener('sendIntentReceived', checkIntentMessage)

    return () => {
      window.removeEventListener('sendIntentReceived', checkIntentMessage)
      // 🚫 SendIntent is {} please don't use it here it makes app crash
      // SendIntent.finish()
    }
  }, [authUser?.id, checkIntentMessage])
}
